
import React, { useState } from "react";
import Header from "@/components/Header";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import PartSubmissionForm from "@/components/submission/PartSubmissionForm";
import GuideSubmissionForm from "@/components/submission/GuideSubmissionForm";
import { SubmissionContent } from "@/types/submission";

const Contribute = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("part");
  
  const handleSubmit = async (type: "part" | "guide", content: SubmissionContent) => {
    if (!user) {
      toast.error("You must be logged in to contribute");
      navigate("/auth");
      return;
    }

    try {
      setIsSubmitting(true);
      
      const { error } = await supabase
        .from("submissions")
        .insert({
          type,
          user_id: user.id,
          content
        });
      
      if (error) throw error;
      
      toast.success("Your submission has been received and is pending review");
      // Navigate to a success page or reset the form
      setActiveTab("part"); // Reset to default tab
      
    } catch (error: any) {
      toast.error(`Error submitting contribution: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header useBackButton={true} pageName="Contribute" />
      
      <div className="container py-8 pt-[64px]">
        <Card className="w-full max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle>Contribute to 3DP Hub</CardTitle>
            <CardDescription>
              Share your 3D printer parts or guides with the community.
              Submissions will be reviewed by administrators before being published.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="part" value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="part">Submit a Part</TabsTrigger>
                <TabsTrigger value="guide">Submit a Guide</TabsTrigger>
              </TabsList>
              <TabsContent value="part">
                <PartSubmissionForm 
                  onSubmit={(content) => handleSubmit("part", content)} 
                  isSubmitting={isSubmitting} 
                />
              </TabsContent>
              <TabsContent value="guide">
                <GuideSubmissionForm 
                  onSubmit={(content) => handleSubmit("guide", content)} 
                  isSubmitting={isSubmitting} 
                />
              </TabsContent>
            </Tabs>
          </CardContent>
          <CardFooter className="flex justify-between border-t pt-6">
            <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default Contribute;
